/**
 * useLoadingTexts Hook
 * Fetches product-specific loading texts from the API and cycles through them
 * at a fixed interval while the analysis is processing.
 */

import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/api/client';

/**
 * Loading text assigned to a product (from GET /api/products/{productId}/loadingtexts)
 */
interface ProductLoadingText {
  id: number;
  loadingTextId: number;
  text: string;
  language: string;
  displayOrder: number;
}

/** Rotation interval: cycle to the next text every 10 seconds */
const ROTATION_INTERVAL = 10_000;

export interface UseLoadingTextsReturn {
  /** The currently displayed loading text (null if none available) */
  currentText: string | null;
  /** All available loading texts in display order */
  texts: string[];
  /** Index of the currently displayed text */
  currentIndex: number;
}

/**
 * Hook that fetches loading texts for a product and cycles through them.
 *
 * @param productId - The product ID to fetch loading texts for
 * @param enabled - Whether to fetch and cycle (typically tied to isPolling)
 */
export function useLoadingTexts(
  productId: number | undefined,
  enabled: boolean,
): UseLoadingTextsReturn {
  const { i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Use base language code (e.g. "de" from "de-DE") to match backend values
  const baseLang = i18n.language?.split('-')[0] ?? 'de';

  const { data: loadingTexts } = useQuery({
    queryKey: ['loadingTexts', productId, baseLang],
    queryFn: () =>
      apiClient.get<ProductLoadingText[]>(
        `/api/products/${productId}/loadingtexts`,
      ),
    enabled: !!productId && enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });

  // Filter by current language and sort by displayOrder
  const texts = useMemo(() => {
    if (!loadingTexts?.length) return [];
    return loadingTexts
      .filter((item) => item.language === baseLang)
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((item) => item.text);
  }, [loadingTexts, baseLang]);

  // Reset index when the text list changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [texts]);

  // Rotate through texts every ROTATION_INTERVAL ms
  useEffect(() => {
    if (texts.length <= 1 || !enabled) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % texts.length);
    }, ROTATION_INTERVAL);
    return () => clearInterval(interval);
  }, [texts.length, enabled]);

  return {
    currentText: texts.length > 0 ? texts[currentIndex] ?? null : null,
    texts,
    currentIndex,
  };
}
